import React, { useCallback, useEffect, useState } from 'react';
import {
  addInquiryAttachments,
  createCrmContact,
  createFollowUp,
  createInquiryCustomer,
  deleteCrmContact,
  fetchAppointmentTypes,
  fetchInquiry,
  fetchInquiryAttachmentUrl,
  fetchInquiryTimeline,
  fetchFollowUps,
  fetchSalesUsers,
  fetchSchedulingSettings,
  fetchUsers,
  linkInquiryCustomer,
  logCrmActivity,
  recordInquiryConversion,
  sendInquiryEmail,
  updateFollowUp,
  updateInquiry,
  updateInquiryAttachment,
} from '../api';
import NewWorkOrderModal from '../newworkordermodal';
import NewJobModal from '../jobs/newjobmodal';
import AppointmentModal from '../scheduling/appointmentmodal';
import {
  errorMessage,
  formatDateTime,
  formatShortDate,
  KIND_OPTIONS,
  localDateInput,
  STATUS_OPTIONS,
  statusClass,
  tenDigitPhone,
} from './inboxFormat';

// One inquiry: who, what they asked, the app customer it belongs to, what it turned
// into, follow-ups, contacts and everything said (notes, calls, Gmail).

const wheelCount = (inq) => {
  const d = inq.details || {};
  const n = Number(d.wheel_count || d.job_wheel_count || d.quantity);
  return Number.isFinite(n) && n > 0 ? n : '';
};

const summaryText = (inq) => {
  const lines = [`${inq.kind_label} inquiry from ${inq.contact_name}${inq.company_name ? ` (${inq.company_name})` : ''}`];
  (inq.details_display || []).forEach((row) => lines.push(`${row.label}: ${row.value}`));
  return lines.join('\n');
};

// What staff can attach (the backend's STAFF_UPLOADS) and how much at once.
const STAFF_FILE_TYPES = 'image/*,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt';
const MAX_FILES = 8;
const MAX_TOTAL_BYTES = 18 * 1024 * 1024;

const filesProblem = (files) => {
  if (files.length > MAX_FILES) return `Attach no more than ${MAX_FILES} files.`;
  if (files.reduce((sum, f) => sum + f.size, 0) > MAX_TOTAL_BYTES) return 'These files add up to more than 18 MB.';
  return '';
};

// Files are private, so each one is fetched with the user's sign-in and shown inside the
// app. (A new tab can't see these downloads in the phone's home-screen app.)
function FileViewer({ attachment, onClose }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [cantPreview, setCantPreview] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    fetchInquiryAttachmentUrl(attachment.id)
      .then((u) => { objectUrl = u; if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [attachment.id]);

  const isImage = (attachment.content_type || '').startsWith('image/');
  const isPdf = attachment.content_type === 'application/pdf';

  return (
    <div className="inbox-modal-overlay inbox-viewer-overlay" onClick={onClose}>
      <div className="inbox-viewer" onClick={(e) => e.stopPropagation()}>
        <div className="inbox-viewer-head">
          <span className="inbox-viewer-name">{attachment.original_name}</span>
          <div className="inbox-actions">
            {url && <a className="inbox-button" href={url} download={attachment.original_name}>Download</a>}
            <button type="button" className="inbox-button" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="inbox-viewer-body">
          {failed && <div className="inbox-error">This file couldn't be loaded.</div>}
          {!failed && !url && <div className="inbox-muted">Loading…</div>}
          {url && isImage && !cantPreview && (
            <img src={url} alt={attachment.original_name} onError={() => setCantPreview(true)} />
          )}
          {url && isPdf && <iframe src={url} title={attachment.original_name} />}
          {url && ((!isImage && !isPdf) || cantPreview) && (
            <div className="inbox-muted">This file can't be shown here. Use Download to open it.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FileLink({ attachment, onOpen }) {
  return (
    <button type="button" className="inbox-link inbox-file-link" onClick={() => onOpen(attachment)}>
      📄 {attachment.original_name}
    </button>
  );
}

// A thumbnail for photos (downloaded right away); other files are just their name, and
// are only downloaded when opened.
function Photo({ attachment, onOpen }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!attachment.is_image) return undefined;
    let objectUrl = null;
    let cancelled = false;
    fetchInquiryAttachmentUrl(attachment.id)
      .then((u) => { objectUrl = u; if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [attachment.id, attachment.is_image]);

  if (!attachment.is_image) return <FileLink attachment={attachment} onOpen={onOpen} />;
  if (failed) return <span className="inbox-muted">{attachment.original_name} (couldn't load)</span>;
  if (!url) return <span className="inbox-muted">Loading {attachment.original_name}…</span>;
  return (
    <button type="button" className="inbox-attachment" title={attachment.original_name} onClick={() => onOpen(attachment)}>
      <img src={url} alt={attachment.original_name} />
    </button>
  );
}

function CustomerPicker({ inquiry, onLinked }) {
  const [query, setQuery] = useState(inquiry.company_name || inquiry.contact_name || '');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: inquiry.company_name || inquiry.contact_name || '',
    phone: inquiry.phone || '',
    email: inquiry.email || '',
    address: inquiry.address || '',
  });

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(() => {
      fetchUsers(query.trim()).then((r) => setResults(r.results || [])).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const link = async (customerId) => {
    setBusy(true);
    setError('');
    try {
      onLinked(await linkInquiryCustomer(inquiry.id, customerId));
    } catch (err) {
      setError(errorMessage(err, 'Could not link the customer.'));
    } finally {
      setBusy(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      onLinked(await createInquiryCustomer(inquiry.id, form));
    } catch (err) {
      setError(errorMessage(err, 'Could not create the customer.'));
    } finally {
      setBusy(false);
    }
  };

  if (creating) {
    return (
      <form className="inbox-subform" onSubmit={create}>
        <div className="inbox-section-title">New customer</div>
        {['name', 'phone', 'email', 'address'].map((field) => (
          <label key={field}>
            <span>{field[0].toUpperCase() + field.slice(1)}</span>
            {field === 'address'
              ? <textarea rows={2} value={form[field]} onChange={(e) => setForm({ ...form, [field]: e.target.value })} />
              : <input value={form[field]} required={field === 'name'} onChange={(e) => setForm({ ...form, [field]: e.target.value })} />}
          </label>
        ))}
        {error && <div className="inbox-error">{error}</div>}
        <div className="inbox-actions">
          <button type="submit" className="inbox-button inbox-button-primary" disabled={busy}>Create customer</button>
          <button type="button" className="inbox-button" onClick={() => setCreating(false)}>Back</button>
        </div>
      </form>
    );
  }

  return (
    <div className="inbox-subform">
      <input type="search" placeholder="Search existing customers" value={query} onChange={(e) => setQuery(e.target.value)} />
      {results.length > 0 && (
        <ul className="inbox-picker-results">
          {results.slice(0, 8).map((c) => (
            <li key={c.id}>
              <span>
                <strong>{c.name}</strong>
                <span className="inbox-muted"> {[c.phone, c.email].filter(Boolean).join(' · ')}</span>
              </span>
              <button type="button" className="inbox-button" disabled={busy} onClick={() => link(c.id)}>Link</button>
            </li>
          ))}
        </ul>
      )}
      {error && <div className="inbox-error">{error}</div>}
      <button type="button" className="inbox-button" onClick={() => setCreating(true)}>+ New customer from this inquiry</button>
    </div>
  );
}

function Composer({ inquiry, gmailConfigured, replyTo, onCancelReply, onSaved }) {
  const [mode, setMode] = useState('note');
  const [text, setText] = useState('');
  const [to, setTo] = useState(inquiry.email || '');
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // [{ file, isQuote }] sent with the email.
  const [attachments, setAttachments] = useState([]);

  const addAttachments = (list) => {
    const next = [...attachments, ...Array.from(list || []).map((file) => ({ file, isQuote: false }))];
    const problem = filesProblem(next.map((a) => a.file));
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    setAttachments(next);
  };

  useEffect(() => {
    if (!replyTo) return;
    setMode('email');
    const incoming = replyTo.kind === 'email_in';
    const fromAddress = (replyTo.email_from.match(/<([^>]+)>/) || [null, replyTo.email_from])[1];
    setTo(incoming ? fromAddress : (replyTo.email_to.split(',')[0] || inquiry.email));
    const subj = replyTo.email_subject || '';
    setSubject(/^re:/i.test(subj) ? subj : `Re: ${subj}`);
  }, [replyTo, inquiry.email]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (mode === 'email') {
        const sent = await sendInquiryEmail(inquiry.id, {
          to, subject, body: text, reply_to_activity: replyTo ? replyTo.id : undefined,
        }, attachments);
        setAttachments([]);
        setNotice(sent.warning || '');
        onCancelReply();
      } else {
        await logCrmActivity({ kind: mode, inquiry: inquiry.id, body: text });
      }
      setText('');
      onSaved();
    } catch (err) {
      setError(errorMessage(err, mode === 'email' ? 'The email could not be sent.' : 'Could not save.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="inbox-composer" onSubmit={submit}>
      <div className="inbox-composer-tabs">
        {[['note', 'Note'], ['call', 'Log a call'], ['email', 'Email']].map(([key, label]) => (
          <button key={key} type="button" className={`inbox-tab ${mode === key ? 'active' : ''}`} onClick={() => { setMode(key); if (key !== 'email') onCancelReply(); }}>
            {label}
          </button>
        ))}
      </div>
      {mode === 'email' && (
        <>
          {!gmailConfigured && <div className="inbox-notice">Gmail isn't connected yet, so this can't be sent.</div>}
          {replyTo && (
            <div className="inbox-muted">
              Replying in the thread &ldquo;{replyTo.email_subject || replyTo.summary}&rdquo;{' '}
              <button type="button" className="inbox-link" onClick={onCancelReply}>new email instead</button>
            </div>
          )}
          <input type="email" placeholder="To" value={to} onChange={(e) => setTo(e.target.value)} required />
          <input type="text" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} required />
        </>
      )}
      <textarea
        rows={mode === 'email' ? 6 : 3}
        placeholder={mode === 'email' ? 'Write your email (sent from info@)' : mode === 'call' ? 'What was said?' : 'Add a note'}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {mode === 'email' && (
        <div className="inbox-email-files">
          {attachments.map((a, i) => (
            <div key={`${a.file.name}-${i}`} className="inbox-email-file">
              <span className="inbox-email-file-name">📎 {a.file.name}</span>
              <label className="inbox-email-file-quote">
                <input
                  type="checkbox"
                  checked={a.isQuote}
                  onChange={(e) => setAttachments(attachments.map((b, j) => (j === i ? { ...b, isQuote: e.target.checked } : b)))}
                />
                Quote
              </label>
              <button type="button" className="inbox-link" onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}>remove</button>
            </div>
          ))}
          <label className="inbox-link inbox-file-picker">
            📎 Attach files
            <input
              type="file"
              multiple
              accept={STAFF_FILE_TYPES}
              onChange={(e) => { addAttachments(e.target.files); e.target.value = ''; }}
            />
          </label>
          {attachments.length > 0 && (
            <div className="inbox-muted">Tick Quote to keep a file with this inquiry's quotes.</div>
          )}
        </div>
      )}
      {error && <div className="inbox-error">{error}</div>}
      {notice && <div className="inbox-notice">{notice}</div>}
      <div className="inbox-actions">
        <button type="submit" className="inbox-button inbox-button-primary" disabled={busy || !text.trim() || (mode === 'email' && !gmailConfigured)}>
          {mode === 'email' ? (busy ? 'Sending…' : 'Send email') : 'Save'}
        </button>
      </div>
    </form>
  );
}

function Timeline({ items, onReply, onOpenFile }) {
  if (!items.length) return <div className="inbox-muted">Nothing yet.</div>;
  return (
    <ul className="inbox-timeline">
      {items.map((a) => (
        <li key={a.id} className={`inbox-activity inbox-activity-${a.kind}`}>
          <div className="inbox-activity-head">
            <span className="inbox-activity-kind">{a.kind_label}</span>
            <span className="inbox-muted">
              {formatDateTime(a.occurred_at)}
              {a.created_by ? ` · ${a.created_by.name}` : ''}
            </span>
          </div>
          {(a.kind === 'email_in' || a.kind === 'email_out') ? (
            <>
              <div className="inbox-activity-summary">{a.email_subject || a.summary}</div>
              <div className="inbox-muted">{a.kind === 'email_in' ? `From ${a.email_from}` : `To ${a.email_to}`}</div>
            </>
          ) : (
            a.summary && a.summary !== a.body && <div className="inbox-activity-summary">{a.summary}</div>
          )}
          {a.body && <div className="inbox-activity-body">{a.body}</div>}
          {a.attachments && a.attachments.length > 0 && (
            <div className="inbox-activity-files">
              {a.attachments.map((f) => (
                <span key={f.id}>
                  <FileLink attachment={f} onOpen={onOpenFile} />
                  {f.is_quote && <span className="inbox-quote-tag">Quote</span>}
                </span>
              ))}
            </div>
          )}
          {a.can_reply && (
            <button type="button" className="inbox-link" onClick={() => onReply(a)}>Reply</button>
          )}
        </li>
      ))}
    </ul>
  );
}

function InquiryDetail({ inquiryId, currentUser, gmailConfigured, onBack, onChanged }) {
  const [inquiry, setInquiry] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [notes, setNotes] = useState({ staff_notes: '', quote_reference: '' });
  const [changingCustomer, setChangingCustomer] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [newFollowUp, setNewFollowUp] = useState(null);
  const [newContact, setNewContact] = useState(null);
  // { type: 'work_order' | 'job' | 'appointment', data }: the prefill is built once when
  // Convert is clicked, so later refreshes of this panel can't reset the open form.
  const [convert, setConvert] = useState(null);
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [viewing, setViewing] = useState(null);

  const loadTimeline = useCallback(() => {
    fetchInquiryTimeline(inquiryId).then(setTimeline).catch(() => {});
    fetchFollowUps({ inquiry: inquiryId, open: 1 }).then(setFollowUps).catch(() => {});
  }, [inquiryId]);

  const applyInquiry = useCallback((data) => {
    setInquiry(data);
    setNotes({ staff_notes: data.staff_notes || '', quote_reference: data.quote_reference || '' });
  }, []);

  useEffect(() => {
    fetchInquiry(inquiryId).then(applyInquiry).catch(() => setError('Could not load this inquiry.'));
    fetchSalesUsers().then(setSalesUsers).catch(() => {});
    loadTimeline();
  }, [inquiryId, applyInquiry, loadTimeline]);

  const save = async (data) => {
    setError('');
    try {
      applyInquiry(await updateInquiry(inquiryId, data));
      loadTimeline();
      onChanged();
      return true;
    } catch (err) {
      setError(errorMessage(err, 'Could not save.'));
      return false;
    }
  };

  const setQuote = async (attachment, isQuote) => {
    setError('');
    try {
      await updateInquiryAttachment(attachment.id, { is_quote: isQuote });
      applyInquiry(await fetchInquiry(inquiryId));
      loadTimeline();
    } catch (err) {
      setError(errorMessage(err, 'Could not change that file.'));
    }
  };

  const addQuoteFiles = async (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const problem = filesProblem(files);
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    setUploadingQuote(true);
    try {
      applyInquiry(await addInquiryAttachments(inquiryId, files, true));
      loadTimeline();
    } catch (err) {
      setError(errorMessage(err, 'Could not add the file.'));
    } finally {
      setUploadingQuote(false);
    }
  };

  const afterLinked = (data) => {
    applyInquiry(data);
    setChangingCustomer(false);
    loadTimeline();
    onChanged();
  };

  const recordConversion = async (data) => {
    setConvert(null);
    try {
      applyInquiry(await recordInquiryConversion(inquiryId, data));
      loadTimeline();
      onChanged();
    } catch (err) {
      setError(errorMessage(err, 'It was created, but could not be added to this inquiry.'));
    }
  };

  const startConvert = async (type) => {
    const customer = inquiry.customer_detail;
    const description = summaryText(inquiry);
    const vehicle = inquiry.details?.vehicle || inquiry.details?.job_vehicle || '';
    if (type === 'work_order') {
      setConvert({ type, data: {
        customer: customer.id,
        customer_name: customer.name,
        phone: tenDigitPhone(customer.phone || inquiry.phone),
        customer_email: customer.email || inquiry.email,
        vehicle,
        number_of_wheels: wheelCount(inquiry),
        description,
      } });
    } else if (type === 'job') {
      setConvert({ type, data: {
        customer: customer.id,
        customer_name: customer.name,
        make: vehicle,
        quantity: wheelCount(inquiry),
        customer_notes: description,
      } });
    } else {
      try {
        const [types, settings] = await Promise.all([fetchAppointmentTypes(true), fetchSchedulingSettings()]);
        setConvert({ type, data: {
          types: types || [],
          slot: settings ? settings.slot_interval_minutes : 30,
          initialValues: {
            customer: { id: customer.id, name: customer.name },
            customer_phone: customer.phone || inquiry.phone,
            customer_email: customer.email || inquiry.email,
            vehicle,
            number_of_wheels: wheelCount(inquiry) || 1,
            description,
          },
        } });
      } catch {
        setError('Could not load the scheduling settings.');
      }
    }
  };

  if (error && !inquiry) return <div className="inbox-detail"><div className="inbox-error">{error}</div></div>;
  if (!inquiry) return <div className="inbox-detail"><div className="inbox-muted">Loading…</div></div>;

  const customer = inquiry.customer_detail;
  const quoteFiles = inquiry.attachments.filter((a) => a.is_quote);
  const otherFiles = inquiry.attachments.filter((a) => !a.is_quote);

  return (
    <div className="inbox-detail">
      <button type="button" className="inbox-link inbox-back" onClick={onBack}>‹ Back to inbox</button>

      <div className="inbox-detail-head">
        <div>
          <h2>{inquiry.display_name}</h2>
          <div className="inbox-muted">
            {inquiry.kind_label} · {inquiry.source === 'website' ? 'Website form' : 'Entered by staff'} · {formatDateTime(inquiry.created_at)}
            {inquiry.marketing_channel && inquiry.source === 'website' ? ` · came from ${inquiry.marketing_channel}${inquiry.marketing_source ? ` (${inquiry.marketing_source})` : ''}` : ''}
          </div>
        </div>
        <div className="inbox-head-controls">
          <label>
            <span>Status</span>
            <select className={statusClass(inquiry.status)} value={inquiry.status} onChange={(e) => save({ status: e.target.value })}>
              {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>
            <span>Assigned</span>
            <select value={inquiry.assigned_to || ''} onChange={(e) => save({ assigned_to: e.target.value ? Number(e.target.value) : null })}>
              <option value="">Nobody</option>
              {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.id === currentUser.id ? `${u.name} (me)` : u.name}</option>)}
            </select>
          </label>
        </div>
      </div>

      {error && <div className="inbox-error">{error}</div>}

      <div className="inbox-grid">
        <div className="inbox-col">
          <section className="inbox-card">
            <div className="inbox-card-head">
              <div className="inbox-section-title">Contact</div>
              {!editing && (
                <button type="button" className="inbox-link" onClick={() => {
                  setEditForm({
                    kind: inquiry.kind, company_name: inquiry.company_name, contact_name: inquiry.contact_name,
                    contact_role: inquiry.contact_role, email: inquiry.email, phone: inquiry.phone, address: inquiry.address,
                  });
                  setEditing(true);
                }}>Edit</button>
              )}
            </div>
            {editing ? (
              <form className="inbox-subform" onSubmit={async (e) => { e.preventDefault(); if (await save(editForm)) setEditing(false); }}>
                <label><span>Type</span>
                  <select value={editForm.kind} onChange={(e) => setEditForm({ ...editForm, kind: e.target.value })}>
                    {KIND_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                {[['company_name', 'Company'], ['contact_name', 'Name'], ['contact_role', 'Role'], ['email', 'Email'], ['phone', 'Phone'], ['address', 'Address']].map(([field, label]) => (
                  <label key={field}><span>{label}</span>
                    <input value={editForm[field] || ''} onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })} />
                  </label>
                ))}
                <div className="inbox-actions">
                  <button type="submit" className="inbox-button inbox-button-primary">Save</button>
                  <button type="button" className="inbox-button" onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </form>
            ) : (
              <dl className="inbox-facts">
                <dt>Name</dt><dd>{inquiry.contact_name}{inquiry.contact_role ? ` (${inquiry.contact_role})` : ''}</dd>
                {inquiry.company_name && <><dt>{inquiry.kind === 'insurance' ? 'Insurance co.' : 'Company'}</dt><dd>{inquiry.company_name}</dd></>}
                <dt>Email</dt><dd>{inquiry.email ? <a href={`mailto:${inquiry.email}`}>{inquiry.email}</a> : '—'}</dd>
                <dt>Phone</dt><dd>{inquiry.phone ? <a href={`tel:${inquiry.phone}`}>{inquiry.phone}</a> : '—'}</dd>
                {inquiry.address && <><dt>Address</dt><dd className="inbox-prewrap">{inquiry.address}</dd></>}
              </dl>
            )}
          </section>

          {inquiry.details_display.length > 0 && (
            <section className="inbox-card">
              <div className="inbox-section-title">What they asked</div>
              <dl className="inbox-facts">
                {inquiry.details_display.map((row) => (
                  <React.Fragment key={row.key}><dt>{row.label}</dt><dd className="inbox-prewrap">{row.value}</dd></React.Fragment>
                ))}
              </dl>
            </section>
          )}

          {otherFiles.length > 0 && (
            <section className="inbox-card">
              <div className="inbox-section-title">Photos & files</div>
              <div className="inbox-attachments">
                {otherFiles.map((a) => (
                  <div key={a.id} className="inbox-attachment-item">
                    <Photo attachment={a} onOpen={setViewing} />
                    {a.source !== 'website' && <div className="inbox-muted">{a.source_label}</div>}
                    <button type="button" className="inbox-link" onClick={() => setQuote(a, true)}>Mark as quote</button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="inbox-card">
            <div className="inbox-card-head">
              <div className="inbox-section-title">Customer</div>
              {customer && !changingCustomer && (
                <button type="button" className="inbox-link" onClick={() => setChangingCustomer(true)}>Change</button>
              )}
            </div>
            {customer && !changingCustomer ? (
              <div>
                <strong>{customer.name}</strong>
                <div className="inbox-muted">{[customer.phone, customer.email].filter(Boolean).join(' · ')}</div>
              </div>
            ) : (
              <>
                {!customer && <div className="inbox-muted">Not linked to an app customer yet. Link an existing one or create a new one.</div>}
                <CustomerPicker inquiry={inquiry} onLinked={afterLinked} />
                {customer && (
                  <div className="inbox-actions">
                    <button type="button" className="inbox-button" onClick={async () => afterLinked(await linkInquiryCustomer(inquiry.id, null))}>Unlink</button>
                    <button type="button" className="inbox-button" onClick={() => setChangingCustomer(false)}>Cancel</button>
                  </div>
                )}
              </>
            )}
          </section>

          <section className="inbox-card">
            <div className="inbox-section-title">Convert</div>
            {!customer && <div className="inbox-muted">Link or create the customer first.</div>}
            <div className="inbox-actions">
              <button type="button" className="inbox-button" disabled={!customer} onClick={() => startConvert('work_order')}>Work order</button>
              <button type="button" className="inbox-button" disabled={!customer} onClick={() => startConvert('job')}>Job</button>
              <button type="button" className="inbox-button" disabled={!customer} onClick={() => startConvert('appointment')}>Appointment</button>
            </div>
            {inquiry.conversions.length > 0 && (
              <ul className="inbox-plain-list">
                {inquiry.conversions.map((c) => (
                  <li key={c.id}>{c.label} <span className="inbox-muted">· {formatShortDate(c.created_at)}{c.created_by ? ` · ${c.created_by.name}` : ''}</span></li>
                ))}
              </ul>
            )}
          </section>

          <section className="inbox-card">
            <div className="inbox-section-title">Quote & notes</div>
            <ul className="inbox-plain-list inbox-quote-files">
              {quoteFiles.map((a) => (
                <li key={a.id}>
                  <FileLink attachment={a} onOpen={setViewing} />
                  <div className="inbox-muted">
                    {a.source_label} · {formatShortDate(a.created_at)}{a.uploaded_by ? ` · ${a.uploaded_by.name}` : ''}{' '}
                    <button type="button" className="inbox-link" onClick={() => setQuote(a, false)}>not a quote</button>
                  </div>
                </li>
              ))}
            </ul>
            <label className="inbox-link inbox-file-picker">
              {uploadingQuote ? 'Adding…' : '+ Add quote file'}
              <input
                type="file"
                multiple
                accept={STAFF_FILE_TYPES}
                disabled={uploadingQuote}
                onChange={(e) => { addQuoteFiles(e.target.files); e.target.value = ''; }}
              />
            </label>
            <label className="inbox-inline-field">
              <span>QuickBooks estimate #</span>
              <input value={notes.quote_reference} onChange={(e) => setNotes({ ...notes, quote_reference: e.target.value })}
                onBlur={() => notes.quote_reference !== (inquiry.quote_reference || '') && save({ quote_reference: notes.quote_reference })} />
            </label>
            <textarea rows={3} placeholder="Staff notes (only staff see these)" value={notes.staff_notes}
              onChange={(e) => setNotes({ ...notes, staff_notes: e.target.value })}
              onBlur={() => notes.staff_notes !== (inquiry.staff_notes || '') && save({ staff_notes: notes.staff_notes })} />
          </section>
        </div>

        <div className="inbox-col">
          <section className="inbox-card">
            <div className="inbox-card-head">
              <div className="inbox-section-title">Follow-ups</div>
              {!newFollowUp && (
                <button type="button" className="inbox-link" onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  setNewFollowUp({ due_date: localDateInput(tomorrow), note: '', assigned_to: currentUser.id });
                }}>+ Add</button>
              )}
            </div>
            {newFollowUp && (
              <form className="inbox-subform" onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await createFollowUp({ ...newFollowUp, inquiry: inquiry.id });
                  setNewFollowUp(null);
                  loadTimeline();
                  onChanged();
                } catch (err) {
                  setError(errorMessage(err, 'Could not add the follow-up.'));
                }
              }}>
                <div className="inbox-row-fields">
                  <input type="date" value={newFollowUp.due_date} required onChange={(e) => setNewFollowUp({ ...newFollowUp, due_date: e.target.value })} />
                  <select value={newFollowUp.assigned_to || ''} onChange={(e) => setNewFollowUp({ ...newFollowUp, assigned_to: e.target.value ? Number(e.target.value) : null })}>
                    {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <input placeholder="e.g. Call back about pickup days" value={newFollowUp.note} required onChange={(e) => setNewFollowUp({ ...newFollowUp, note: e.target.value })} />
                <div className="inbox-actions">
                  <button type="submit" className="inbox-button inbox-button-primary">Add follow-up</button>
                  <button type="button" className="inbox-button" onClick={() => setNewFollowUp(null)}>Cancel</button>
                </div>
              </form>
            )}
            {followUps.length === 0 && !newFollowUp && <div className="inbox-muted">None open.</div>}
            <ul className="inbox-plain-list">
              {followUps.map((f) => (
                <li key={f.id} className="inbox-follow-up">
                  <label>
                    <input type="checkbox" onChange={async () => { await updateFollowUp(f.id, { done: true }); loadTimeline(); onChanged(); }} />
                    <span className={f.due_date <= localDateInput() ? 'inbox-due-date' : ''}>{formatShortDate(f.due_date)}</span>
                    <span>{f.note}</span>
                    {f.assigned_to_detail && <span className="inbox-muted">{f.assigned_to_detail.name}</span>}
                  </label>
                </li>
              ))}
            </ul>
          </section>

          <section className="inbox-card">
            <div className="inbox-card-head">
              <div className="inbox-section-title">Contacts</div>
              {!newContact && <button type="button" className="inbox-link" onClick={() => setNewContact({ name: '', role: '', email: '', phone: '' })}>+ Add</button>}
            </div>
            {newContact && (
              <form className="inbox-subform" onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await createCrmContact({ ...newContact, inquiry: inquiry.id, customer: inquiry.customer || null });
                  setNewContact(null);
                  applyInquiry(await fetchInquiry(inquiry.id));
                } catch (err) {
                  setError(errorMessage(err, 'Could not add the contact.'));
                }
              }}>
                {[['name', 'Name'], ['role', 'Role'], ['email', 'Email'], ['phone', 'Phone']].map(([field, label]) => (
                  <input key={field} placeholder={label} required={field === 'name'} value={newContact[field]}
                    onChange={(e) => setNewContact({ ...newContact, [field]: e.target.value })} />
                ))}
                <div className="inbox-actions">
                  <button type="submit" className="inbox-button inbox-button-primary">Add contact</button>
                  <button type="button" className="inbox-button" onClick={() => setNewContact(null)}>Cancel</button>
                </div>
              </form>
            )}
            <ul className="inbox-plain-list">
              {inquiry.contacts.map((c) => (
                <li key={c.id}>
                  <strong>{c.name}</strong>{c.role ? ` (${c.role})` : ''}
                  <div className="inbox-muted">
                    {[c.email, c.phone].filter(Boolean).join(' · ')}{' '}
                    <button type="button" className="inbox-link" onClick={async () => {
                      await deleteCrmContact(c.id);
                      applyInquiry(await fetchInquiry(inquiry.id));
                    }}>remove</button>
                  </div>
                </li>
              ))}
            </ul>
            {inquiry.contacts.length === 0 && !newContact && <div className="inbox-muted">Their emails are logged here from info@ too.</div>}
          </section>

          <section className="inbox-card">
            <div className="inbox-section-title">Activity</div>
            <Composer
              inquiry={inquiry}
              gmailConfigured={gmailConfigured}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              onSaved={() => { loadTimeline(); fetchInquiry(inquiry.id).then(applyInquiry); onChanged(); }}
            />
            <Timeline items={timeline} onReply={(a) => setReplyTo(a)} onOpenFile={setViewing} />
          </section>
        </div>
      </div>

      <NewWorkOrderModal
        showModal={convert?.type === 'work_order'}
        toggleModal={() => setConvert(null)}
        onWorkOrderCreated={(wo) => wo && wo.id && recordConversion({ work_order: wo.id })}
        initialData={convert?.type === 'work_order' ? convert.data : null}
      />
      {convert?.type === 'job' && (
        <NewJobModal
          showModal
          toggleModal={() => setConvert(null)}
          onJobCreated={(job) => job && job.id && recordConversion({ job: job.id })}
          initialData={convert.data}
        />
      )}
      {viewing && <FileViewer attachment={viewing} onClose={() => setViewing(null)} />}
      {convert?.type === 'appointment' && (
        <AppointmentModal
          appointment={null}
          prefilledStart={null}
          appointmentTypes={convert.data.types}
          slotIntervalMinutes={convert.data.slot}
          initialValues={convert.data.initialValues}
          onCreated={(appt) => appt && appt.id && recordConversion({ appointment: appt.id })}
          onClose={() => setConvert(null)}
        />
      )}
    </div>
  );
}

export default InquiryDetail;
